import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowUpDown, XCircle, Save, Edit, Trash2, Plus, SmilePlus, Key } from 'lucide-react';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import { useCCContext } from '../CC/CCContext';
import styles from './UserManagement.module.css';

interface User {
    id: string;
    realname: string;
    email: string;
    created: string;
    DateLastLogin: string;
    roles: string[];
}

type UserManagementProps = {
    onClose?: () => void;
};

const UserManagement: React.FC<UserManagementProps> = ({ onClose }) => {
    const { user } = useCCContext();
    const [users, setUsers] = useState<User[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: keyof User; direction: 'asc' | 'desc' } | null>(null);
    const [filters, setFilters] = useState({ realname: '', email: '' });
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    
    // State for Password Change Modal
    const [passwordChangeModal, setPasswordChangeModal] = useState<{ isOpen: boolean; userId: string } | null>(null);
    const [newPassword, setNewPassword] = useState<string>('');
    const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
    const [passwordChangeLoading, setPasswordChangeLoading] = useState<boolean>(false);
    
    // State for Add User Modal
    const [isAddUserOpen, setIsAddUserOpen] = useState<boolean>(false);
    const [newUser, setNewUser] = useState<{ realname: string; email: string; password: string }>({
        realname: '',
        email: '',
        password: '',
    });
    const [addUserError, setAddUserError] = useState<string | null>(null);
    const [addUserLoading, setAddUserLoading] = useState<boolean>(false);

    // State for Role Management Modal
    const [roleModal, setRoleModal] = useState<{ isOpen: boolean; userId: string; action: 'add' | 'revoke'; role?: string } | null>(null);
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [roleLoading, setRoleLoading] = useState<boolean>(false);
    const [roleError, setRoleError] = useState<string | null>(null);

    // List of available roles (can be fetched from the server if dynamic)
    const availableRoles = ['admin', 'user', 'editor', 'viewer'];

    // Prevent body scrolling when the popup is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=getusers&token=${user.token}`);
            if (!response.ok) {
                throw new Error(`Error fetching users: ${response.statusText}`);
            }
            const data = await response.json();
            // Map backend data to frontend User interface
            const mappedUsers = data.users.map((u: any) => ({
                id: u.id,
                realname: u.realname,
                email: u.email,
                created: u.created,
                DateLastLogin: u.lastlogin,
                roles: u.roles, // Map roles from backend
            }));
            setUsers(mappedUsers);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users. Please try again later.');
        } finally {
            setLoading(false);
        }
    };
    
    // Fetch users data from backend API
    useEffect(() => {
        if (user.token) {
            fetchUsers();
        }
    }, [user.token]);

    const handlePasswordChange = async () => {
        if (!passwordChangeModal) return;
        setPasswordChangeLoading(true);
        setPasswordChangeError(null);
        
        const userEmail = users.find(u => u.id === passwordChangeModal.userId)?.email;
        if (!userEmail) {
            setPasswordChangeError('User not found');
            setPasswordChangeLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=changepasswordbyadmin&token=${user.token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    newpassword: newPassword
                }),
            });

            if (!response.ok) {
                throw new Error(`Error changing password: ${response.statusText}`);
            }

            const result = await response.text();
            if (result === "Password changed successfully.") {
                setPasswordChangeModal(null);
                setNewPassword('');
            } else {
                throw new Error(result || 'Failed to change password.');
            }
        } catch (err) {
            console.error('Error changing password:', err);
            setPasswordChangeError('Failed to change password. Please try again.');
        } finally {
            setPasswordChangeLoading(false);
        }
    };
    
    const handleAddRole = (userId: string) => {
        setRoleModal({ isOpen: true, userId, action: 'add' });
        setSelectedRole('');
        setRoleError(null);
    };

    // Updated handleRevokeRole to accept role
    const handleRevokeRole = (userId: string, role: string) => {
        setRoleModal({ isOpen: true, userId, action: 'revoke', role });
        setSelectedRole(role);
        setRoleError(null);
    };

    const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedRole(e.target.value);
    };

    const handleRoleSubmit = async () => {
        if (!roleModal) return;
        setRoleLoading(true);
        setRoleError(null);
        try {
            let actionEndpoint = '';
            let requestBody: any = { email: users.find(u => u.id === roleModal.userId)?.email };

            if (roleModal.action === 'add') {
                actionEndpoint = 'adduserrole';
                requestBody.role = selectedRole;
            } else if (roleModal.action === 'revoke') {
                actionEndpoint = 'revokeuserrole';
                requestBody.role = roleModal.role; // Use the specific role to revoke
            }

            const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=${actionEndpoint}&token=${user.token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`Error ${roleModal.action === 'add' ? 'adding' : 'revoking'} role: ${response.statusText}`);
            }

            const result = await response.text();
            if (roleModal.action === 'add' && result === 'Role added successfully.') {
                // Update user's roles locally
                setUsers(prevUsers =>
                    prevUsers.map(u =>
                        u.id === roleModal.userId ? { ...u, roles: [...u.roles, selectedRole] } : u
                    )
                );
                setRoleModal(null);
            } else if (roleModal.action === 'revoke' && result === 'Role revoked successfully.') {
                // Update user's roles locally
                setUsers(prevUsers =>
                    prevUsers.map(u =>
                        u.id === roleModal.userId ? { ...u, roles: u.roles.filter(r => r !== roleModal.role) } : u
                    )
                );
                setRoleModal(null);
            } else {
                throw new Error(result || 'Operation failed.');
            }
        } catch (err) {
            console.error(`Error ${roleModal?.action}:`, err);
            setRoleError(`Failed to ${roleModal?.action} role. Please try again.`);
        } finally {
            setRoleLoading(false);
        }
    };
    
    const handleSort = useCallback((key: keyof User) => {
        setSortConfig(prev => {
            if (prev && prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    }, []);

    const handleFilterChange = useCallback((field: 'realname' | 'email', value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleEdit = useCallback((userId: string) => {
        setEditingUserId(userId);
    }, []);

    const handleCancel = useCallback(() => {
        setEditingUserId(null);
    }, []);

    const handleSave = useCallback(
        async (updatedUser: User) => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=changeuserbyadmin&token=${user.token}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: updatedUser.id,
                        email: updatedUser.email,
                        newrealname: updatedUser.realname,
                        newemail: updatedUser.email,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Error saving user: ${response.statusText}`);
                }

                const result = await response.text();
                if (result === 'Account Changed') {
                    setUsers(prevUsers =>
                        prevUsers.map(u => (u.id === updatedUser.id ? updatedUser : u))
                    );
                    setEditingUserId(null);
                } else {
                    throw new Error(result || 'Failed to save user.');
                }
            } catch (err) {
                console.error('Error saving user:', err);
                setError('Failed to save user. Please try again.');
            } finally {
                setLoading(false);
            }
        },
        [user.token]
    );

    const handleDelete = useCallback(
        async (userId: string) => {
            if (!window.confirm('Are you sure you want to delete this user?')) return;
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=deleteuserbyadmin&token=${user.token}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: userId }),
                });

                if (!response.ok) {
                    throw new Error(`Error deleting user: ${response.statusText}`);
                }

                const result = await response.text();
                if (result === 'Account Removed') {
                    setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
                } else {
                    throw new Error(result || 'Failed to delete user.');
                }
            } catch (err) {
                console.error('Error deleting user:', err);
                setError('Failed to delete user. Please try again.');
            } finally {
                setLoading(false);
            }
        },
        [user.token]
    );

    const handleAddUserOpen = () => {
        setIsAddUserOpen(true);
        setAddUserError(null);
        setNewUser({ realname: '', email: '', password: '' });
    };

    const handleAddUserClose = () => {
        setIsAddUserOpen(false);
    };

    const handleAddUserChange = (field: 'realname' | 'email' | 'password', value: string) => {
        setNewUser(prev => ({ ...prev, [field]: value }));
    };

    const handleAddUserSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAddUserLoading(true);
        setAddUserError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}?action=create&token=${user.token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    realname: newUser.realname,
                    email: newUser.email,
                    password: newUser.password,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error creating user: ${response.statusText}`);
            }

            const result = await response.text();
            console.log(result);
            if (result === "Account Created") {
                fetchUsers();
                setIsAddUserOpen(false);
            } else {
                throw new Error(result || 'Failed to create user.');
            }
        } catch (err) {
            console.error('Error creating user:', err);
            setAddUserError('Failed to create user. Please try again.');
        } finally {
            setAddUserLoading(false);
        }
    };

    const filteredAndSortedUsers = useMemo(() => {
        let filtered = users.filter(u =>
            u.realname.toLowerCase().includes(filters.realname.toLowerCase()) &&
            u.email.toLowerCase().includes(filters.email.toLowerCase())
        );

        if (sortConfig) {
            filtered = filtered.sort((a, b) => {
                let aValue: any = a[sortConfig.key];
                let bValue: any = b[sortConfig.key];

                // If sorting by DateLastLogin, convert to Date for comparison
                if (sortConfig.key === 'DateLastLogin') {
                    aValue = aValue ? new Date(aValue) : new Date(0);
                    bValue = bValue ? new Date(bValue) : new Date(0);
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [users, filters, sortConfig]);

    const renderTableHeader = () => {
        const headers: { key: keyof User | 'actions'; label: string }[] = [
            { key: 'realname', label: 'Real Name' },
            { key: 'email', label: 'Email' },
            { key: 'created', label: 'Created' },
            { key: 'DateLastLogin', label: 'Date Last Login' },
            { key: 'roles', label: 'Roles' }, // Added Roles header
            { key: 'actions', label: 'Actions' }, // Added Actions header
        ];

        return headers.map(header => (
            <th
                key={header.key}
                className={styles.th}
                onClick={() => header.key !== 'actions' && handleSort(header.key as keyof User)}
                role={header.key !== 'actions' ? "button" : undefined}
                aria-sort={
                    sortConfig?.key === header.key
                        ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                        : 'none'
                }
            >
                <div className={styles.headerContent}>
                    {header.label}
                    {header.key !== 'actions' && (
                        <ArrowUpDown
                            className={`${styles.sortIcon} ${
                                sortConfig?.key === header.key ? styles.activeSortIcon : ''
                            }`}
                            size={16}
                        />
                    )}
                </div>
            </th>
        ));
    };

    const renderTableRows = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={6} className={styles.loading}>
                        Loading users...
                    </td>
                </tr>
            );
        }

        if (error) {
            return (
                <tr>
                    <td colSpan={6} className={styles.error}>
                        {error}
                    </td>
                </tr>
            );
        }

        if (filteredAndSortedUsers.length === 0) {
            return (
                <tr>
                    <td colSpan={6} className={styles.noData}>
                        No users found.
                    </td>
                </tr>
            );
        }

        return filteredAndSortedUsers.map(user => (
            <tr key={user.id} className={styles.row}>
                <td className={styles.td}>
                    {editingUserId === user.id ? (
                        <input
                            type="text"
                            className={styles.editInput}
                            value={user.realname}
                            onChange={e =>
                                setUsers(prev =>
                                    prev.map(u =>
                                        u.id === user.id ? { ...u, realname: e.target.value } : u
                                    )
                                )
                            }
                        />
                    ) : (
                        user.realname
                    )}
                </td>
                <td className={styles.td}>
                    {editingUserId === user.id ? (
                        <input
                            type="email"
                            className={styles.editInput}
                            value={user.email}
                            onChange={e =>
                                setUsers(prev =>
                                    prev.map(u =>
                                        u.id === user.id ? { ...u, email: e.target.value } : u
                                    )
                                )
                            }
                        />
                    ) : (
                        user.email
                    )}
                </td>
                <td className={styles.td}>{new Date(user.created).toLocaleString()}</td>
                <td className={styles.td}>
                    {user.DateLastLogin ? new Date(user.DateLastLogin).toLocaleString() : 'Never'}
                </td>
                <td className={styles.td}>
                    <div className={styles.rolesContainer}>
                        {user.roles.map(role => (
                            <span key={role} className={styles.role}>
                                {role}
                                {isAdmin && (
                                    <button
                                        className={styles.revokeButton}
                                        onClick={() => handleRevokeRole(user.id, role)}
                                        aria-label={`Revoke ${role} role from ${user.realname}`}
                                    >
                                        <XCircle size={14} />
                                    </button>
                                )}
                            </span>
                        ))}
                    </div>
                </td>
                <td className={styles.td}>
                    {editingUserId === user.id ? (
                        <>
                            <button
                                className={`${styles.actionButton} ${styles.saveButton}`}
                                onClick={() => handleSave(user)}
                                aria-label="Save"
                            >
                                <Save size={16} />
                            </button>
                            <button
                                className={`${styles.actionButton} ${styles.cancelButton}`}
                                onClick={handleCancel}
                                aria-label="Cancel"
                            >
                                <XCircle size={16} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                className={`${styles.actionButton} ${styles.editButton}`}
                                onClick={() => handleEdit(user.id)}
                                aria-label="Edit"
                            >
                                <Edit size={16} />
                            </button>
                            {isAdmin && (
                                <>
                                    <button
                                        className={`${styles.actionButton} ${styles.addRoleButton}`}
                                        onClick={() => handleAddRole(user.id)}
                                        aria-label="Add Role"
                                    >
                                        <SmilePlus size={16} />
                                    </button>
                                    <button
                                        className={`${styles.actionButton} ${styles.passwordButton}`}
                                        onClick={() => setPasswordChangeModal({ isOpen: true, userId: user.id })}
                                        aria-label="Change Password"
                                    >
                                        <Key size={16} />
                                    </button>
                                </>
                            )}
                            <button
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                onClick={() => handleDelete(user.id)}
                                aria-label="Delete"
                            >
                                <Trash2 size={16} />
                            </button>
                        </>
                    )}
                </td>
            </tr>
        ));
    };

    // Check if the current user is an admin
    const isAdmin = user.userDetails.roles.includes('admin');

    return (
        <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-labelledby="user-management-title">
            <div className={styles.popup}>
                <div className={styles.header}>
                    <h2 id="user-management-title" className={styles.title}>User Management</h2>
                    <GenericIconButton
                        icon='light/times.svg'
                        width='18px'
                        height='18px'
                        onClick={onClose}
                        aria-label="Close"
                    />
                </div>

                <div className={styles.main}>
                    <div className={styles.filterContainer}>
                        <input
                            type="text"
                            placeholder="Filter by name..."
                            className={styles.filterInput}
                            value={filters.realname}
                            onChange={e => handleFilterChange('realname', e.target.value)}
                            aria-label="Filter by name"
                        />
                        <input
                            type="text"
                            placeholder="Filter by email..."
                            className={styles.filterInput}
                            value={filters.email}
                            onChange={e => handleFilterChange('email', e.target.value)}
                            aria-label="Filter by email"
                        />
                        <button
                            className={styles.addButton}
                            onClick={handleAddUserOpen}
                            aria-label="Add User"
                        >
                            <Plus size={16} /> Add User
                        </button>
                    </div>

                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {renderTableHeader()}
                                </tr>
                            </thead>
                            <tbody>{renderTableRows()}</tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.footer}>
                    <button className={styles.closeButton} onClick={onClose}>
                        Close
                    </button>
                </div>

                {/* Add User Modal */}
                {isAddUserOpen && (
                    <div className={styles.addUserOverlay} role="dialog" aria-modal="true" aria-labelledby="add-user-title">
                        <div className={styles.addUserPopup}>
                            <div className={styles.addUserHeader}>
                                <h3 id="add-user-title" className={styles.addUserTitle}>Add New User</h3>
                                <GenericIconButton
                                    icon='light/times.svg'
                                    width='18px'
                                    height='18px'
                                    onClick={handleAddUserClose}
                                    aria-label="Close Add User"
                                />
                            </div>
                            <form className={styles.addUserForm} onSubmit={handleAddUserSubmit}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="realname">Name</label>
                                    <input
                                        type="text"
                                        id="realname"
                                        value={newUser.realname}
                                        onChange={e => handleAddUserChange('realname', e.target.value)}
                                        required
                                        className={styles.formInput}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="email">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={newUser.email}
                                        onChange={e => handleAddUserChange('email', e.target.value)}
                                        required
                                        className={styles.formInput}
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        value={newUser.password}
                                        onChange={e => handleAddUserChange('password', e.target.value)}
                                        required
                                        className={styles.formInput}
                                    />
                                </div>
                                {addUserError && <div className={styles.addUserError}>{addUserError}</div>}
                                <div className={styles.formActions}>
                                    <button
                                        type="submit"
                                        className={styles.submitButton}
                                        disabled={addUserLoading}
                                    >
                                        {addUserLoading ? 'Adding...' : 'Add User'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.cancelButton}
                                        onClick={handleAddUserClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Password Change Modal */}
                {passwordChangeModal && passwordChangeModal.isOpen && (
                    <div className={styles.passwordModalOverlay} role="dialog" aria-modal="true" aria-labelledby="password-change-title">
                        <div className={styles.passwordModalPopup}>
                            <div className={styles.passwordModalHeader}>
                                <h3 id="password-change-title" className={styles.passwordModalTitle}>
                                    Change Password
                                </h3>
                                <GenericIconButton
                                    icon='light/times.svg'
                                    width='18px'
                                    height='18px'
                                    onClick={() => {
                                        setPasswordChangeModal(null);
                                        setNewPassword('');
                                        setPasswordChangeError(null);
                                    }}
                                    aria-label="Close Password Modal"
                                />
                            </div>
                            <div className={styles.passwordModalBody}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="newPassword">New Password</label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className={styles.formInput}
                                        required
                                    />
                                </div>
                                {passwordChangeError && (
                                    <div className={styles.passwordError}>{passwordChangeError}</div>
                                )}
                            </div>
                            <div className={styles.passwordModalActions}>
                                <button
                                    className={styles.submitButton}
                                    onClick={handlePasswordChange}
                                    disabled={passwordChangeLoading || !newPassword}
                                >
                                    {passwordChangeLoading ? 'Changing...' : 'Change Password'}
                                </button>
                                <button
                                    className={styles.cancelButton}
                                    onClick={() => {
                                        setPasswordChangeModal(null);
                                        setNewPassword('');
                                        setPasswordChangeError(null);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Role Management Modal */}
                {roleModal && roleModal.isOpen && (
                    <div className={styles.roleModalOverlay} role="dialog" aria-modal="true" aria-labelledby="role-management-title">
                        <div className={styles.roleModalPopup}>
                            <div className={styles.roleModalHeader}>
                                <h3 id="role-management-title" className={styles.roleModalTitle}>
                                    {roleModal.action === 'add' ? 'Add Role' : 'Revoke Role'}
                                </h3>
                                <GenericIconButton
                                    icon='light/times.svg'
                                    width='18px'
                                    height='18px'
                                    onClick={() => setRoleModal(null)}
                                    aria-label="Close Role Modal"
                                />
                            </div>
                            <div className={styles.roleModalBody}>
                                {roleModal.action === 'add' ? (
                                    <>
                                        <label htmlFor="roleSelect">Select Role:</label>
                                        <select
                                            id="roleSelect"
                                            value={selectedRole}
                                            onChange={handleRoleChange}
                                            className={styles.roleSelect}
                                        >
                                            <option value="">--Select Role--</option>
                                            {availableRoles.map(role => (
                                                <option key={role} value={role}>
                                                    {role}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                ) : (
                                    <>
                                        <p>Are you sure you want to revoke the <strong>{roleModal.role}</strong> role from this user?</p>
                                    </>
                                )}
                                {roleError && <div className={styles.roleError}>{roleError}</div>}
                            </div>
                            <div className={styles.roleModalActions}>
                                <button
                                    className={styles.submitRoleButton}
                                    onClick={handleRoleSubmit}
                                    disabled={roleLoading || (roleModal.action === 'add' && !selectedRole)}
                                >
                                    {roleLoading ? (roleModal.action === 'add' ? 'Adding...' : 'Revoking...') : (roleModal.action === 'add' ? 'Add Role' : 'Revoke Role')}
                                </button>
                                <button
                                    className={styles.cancelRoleButton}
                                    onClick={() => setRoleModal(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
